import { FormControl, Stack, Typography } from "@mui/material";
import { FormikValues } from "formik";

import { DecimalTextField } from "../DecimalTextField/DecimalTextField";
import { useFieldError } from "../forms/missing-fields/hooks/useFieldError";
import { FormikDecimalTextFieldProps } from "./FormikDecimalTextField.types";
import { getFieldPath, getFieldValue } from "./FormikField.utils";

function withDecimalScale(padded: string, fixed: boolean): string {
  if (fixed || !padded.includes(".")) {
    return padded;
  }
  return padded.replace(/0+$/, "").replace(/\.$/, "");
}

export function FormikDecimalTextField<T extends FormikValues>(props: FormikDecimalTextFieldProps<T>) {
  const {
    formik,
    label,
    disabled,
    error: errorProp,
    helperText,
    textFieldProps,
    formControlProps,
    required,
    decimalScale,
    fixedDecimalScale = true,
    slotProps,
    onValueChange,
    allowNegative,
    minValue,
    maxValue,
    disableThousandSeparator,
    labelAbove,
    id,
  } = props;

  const fullPath = getFieldPath(
    "path" in props ? props.path : undefined,
    "fieldName" in props ? props.fieldName : undefined
  );
  const fieldValue = getFieldValue(formik, fullPath);
  // Both variants go through toFixed, which never yields the exponent notation `String()` falls back
  // to for very small values and which DecimalTextField cannot parse; unpadded then drops the zeros
  // toFixed added, so a whole number stays whole.
  const value =
    (typeof fieldValue === "number" || typeof fieldValue === "string") && fieldValue !== ""
      ? withDecimalScale(parseFloat(String(fieldValue)).toFixed(decimalScale ?? 2), fixedDecimalScale)
      : "";

  const error = useFieldError(formik, fullPath, label);
  const resolvedHelperText = error ?? helperText;
  const hasError = !!error || !!errorProp;

  const handleChange = (next?: string) => {
    const parsed = next ? parseFloat(next) : null;
    void formik.setFieldValue(fullPath, parsed);
    onValueChange?.(parsed);
  };

  const inputLabelSlotProps = typeof slotProps?.inputLabel === "object" ? slotProps.inputLabel : {};
  const inputSlot = typeof slotProps?.input === "object" ? slotProps.input : {};
  const htmlInputSlot = typeof slotProps?.htmlInput === "object" ? slotProps.htmlInput : undefined;

  if (labelAbove) {
    const labelId = id ? `${id}-label` : undefined;
    return (
      <Stack spacing={0.5}>
        <Typography id={labelId} variant="caption" sx={{ fontWeight: 500 }}>
          {label}
          {required && <span aria-hidden> *</span>}
        </Typography>
        <DecimalTextField
          id={id}
          name={fullPath}
          value={value}
          onChange={handleChange}
          onBlur={formik.handleBlur}
          error={hasError}
          helperText={resolvedHelperText ?? " "}
          size="small"
          disabled={disabled}
          required={required}
          allowNegative={allowNegative}
          minValue={minValue}
          maxValue={maxValue}
          decimalScale={decimalScale}
          slotProps={{
            htmlInput: { "aria-labelledby": labelId },
            input: inputSlot,
          }}
        />
      </Stack>
    );
  }

  return (
    <FormControl fullWidth margin="none" {...formControlProps}>
      <DecimalTextField
        {...textFieldProps}
        id={id}
        label={label}
        name={fullPath}
        value={value}
        onChange={handleChange}
        onBlur={formik.handleBlur}
        error={hasError}
        helperText={resolvedHelperText ?? " "}
        variant="outlined"
        size="small"
        disabled={disabled}
        required={required}
        allowNegative={allowNegative}
        minValue={minValue}
        maxValue={maxValue}
        disableThousandSeparator={disableThousandSeparator}
        slotProps={{
          inputLabel: {
            sx: { fontSize: "0.875rem" },
            ...inputLabelSlotProps,
          },
          input: {
            required: false,
            sx: { fontSize: "0.875rem" },
            ...inputSlot,
          },
          htmlInput: htmlInputSlot,
        }}
        decimalScale={decimalScale}
      />
    </FormControl>
  );
}
